<template>
  <template v-if="product">
    <div class="grey-box">
      <div class="checkout-page-item">
        <img :src="product.imageUrl" class="checkout-page-item-image" />
        <div class="checkout-page-item-info">
          <p class="body-text-bold">
            {{ product.name }}
          </p>
          <p class="body-text-bold">
            {{ store.formatPrice(product.price) }}
          </p>
          <p class="body-text-bold">{{ __('item_quantity') }}: {{ quantity }}</p>

          <p class="body-text-bold primary-text">
            {{ store.formatPrice(product.price * quantity) }}
          </p>
        </div>
      </div>
    </div>

    <form class="checkout-page-form" @submit.prevent="submitPayment">
      <div class="checkout-page-form-box">
        <p class="title-text">{{ __('your_information') }}</p>
        <div class="checkout-page-form-box-row">
          <InputComponent
            :autocomplete="'given-name'"
            v-model="checkoutFormData.name"
            :placeholder="__('form_name')"
            :error-message="validation.name.$error ? __('form_required') : ''"
            :class="{
              'is-invalid': validation.name.$error,
            }" />
          <InputComponent
            :autocomplete="'family-name'"
            v-model="checkoutFormData.surname"
            :placeholder="__('form_surname')"
            :error-message="validation.surname.$error ? __('form_required') : ''"
            :class="{
              'is-invalid': validation.surname.$error,
            }" />
        </div>
        <InputComponent
          :autocomplete="'address-line1'"
          v-model="checkoutFormData.address"
          :placeholder="__('form_adress')"
          :error-message="validation.address.$error ? __('form_required') : ''"
          :class="{
            'is-invalid': validation.address.$error,
          }" />
        <div class="checkout-page-form-box-row">
          <InputComponent
            :autocomplete="'address-level2'"
            v-model="checkoutFormData.city"
            :placeholder="__('form_city')"
            :error-message="validation.city.$error ? __('form_required') : ''"
            :class="{
              'is-invalid': validation.city.$error,
            }" />
          <InputComponent
            :autocomplete="'postal-code'"
            v-model="checkoutFormData.postal"
            :placeholder="__('form_postal_code')"
            :error-message="validation.postal.$error ? __('form_required') : ''"
            :class="{
              'is-invalid': validation.postal.$error,
            }" />
        </div>
      </div>

      <div class="checkout-page-form-box">
        <p class="title-text">{{ __('contact_information') }}</p>
        <div class="checkout-page-form-box-row">
          <InputComponent
            :autocomplete="'email'"
            v-model="checkoutFormData.email"
            :placeholder="__('form_email')"
            :input-type="'email'"
            :error-message="validation.email.$error ? __('form_required') : ''"
            :class="{
              'is-invalid': validation.email.$error,
            }" />
          <InputComponent
            :autocomplete="'tel'"
            v-model="checkoutFormData.phone"
            :placeholder="__('form_phone')"
            :input-type="'tel'"
            :error-message="validation.phone.$error ? __('form_required') : ''"
            :class="{
              'is-invalid': validation.phone.$error,
            }" />
        </div>
      </div>
      <div class="checkout-page-form-box">
        <p
          class="title-text"
          :class="{
            'error-text': validation.paymentMethod.$error,
          }">
          {{ __('payment_method') }}
          <span class="small-list-text error-text" v-if="validation.paymentMethod.$error">
            {{ __('form_required') }}
          </span>
        </p>
        <div class="checkout-page-form-box-radios">
          <div
            class="radio"
            :class="{
              'is-invalid': validation.paymentMethod.$error,
            }">
            <input
              type="radio"
              id="paymentMethod1"
              name="paymentMethod"
              value="stripe"
              v-model="checkoutFormData.paymentMethod" />
            <label for="paymentMethod1">
              <p class="label-hidden-text">{{ __('checkout_option_card') }}</p>
              <p class="label-text">{{ __('checkout_option_card') }}</p>
            </label>
          </div>
          <div
            class="radio"
            :class="{
              'is-invalid': validation.paymentMethod.$error,
            }">
            <input
              type="radio"
              id="paymentMethod2"
              name="paymentMethod"
              value="delivery"
              v-model="checkoutFormData.paymentMethod" />
            <label for="paymentMethod2">
              <p class="label-hidden-text">{{ __('checkout_option_delivery') }}</p>
              <p class="label-text">{{ __('checkout_option_delivery') }}</p>
            </label>
          </div>
        </div>

        <div class="checkout-page-form-box-checkboxes">
          <div
            class="checkbox"
            :class="{
              'is-invalid': validation.termsAndConditions.$error,
            }">
            <input
              type="checkbox"
              id="termsAndConditions"
              :value="true"
              v-model="checkoutFormData.termsAndConditions" />
            <label for="termsAndConditions">
              <p class="list-text">
                {{ __('terms_and_conditions_payment') }}
              </p>
            </label>
          </div>
          <div class="checkbox">
            <input type="checkbox" id="newsletter" :value="true" v-model="checkoutFormData.newsletter" />
            <label for="newsletter">
              {{ __('newsletter_text') }}
            </label>
          </div>
        </div>
        <button class="button" type="submit">
          <img src="@/assets/icons/starIcon.svg" />
          {{ __('product_buy') }}
        </button>
      </div>
    </form>
  </template>

  <NotFoundComponent v-if="notFound" />
</template>

<script lang="ts" setup>
import { generalStore } from '@/store';
import { ref, computed, nextTick, onMounted } from 'vue';
import InputComponent from '@/components/form-components/InputComponent.vue';
import { useRouter } from 'vue-router';
import { useVuelidate, ValidationRule } from '@vuelidate/core';
import { email, required, sameAs } from '@vuelidate/validators';
import axios from 'axios';
import NotFoundComponent from '@/components/NotFoundComponent.vue';
import __ from '@/translate';

const store = generalStore();
const product = ref();
const router = useRouter();
const quantity = ref();
const submitted = ref(false);
const checkoutFormData = ref({
  name: '',
  surname: '',
  address: '',
  postal: '',
  city: '',
  email: '',
  phone: '',
  paymentMethod: '',
  newsletter: false,
  termsAndConditions: false,
  productId: parseInt(router.currentRoute.value.params.id.toString()),
  amount: 0,
  comment: '',
});
const notFound = ref(false);

const validationRules = computed(() => {
  const localRules: Record<string, Record<string, ValidationRule>> = {};

  localRules.name = {
    required,
  };

  localRules.surname = {
    required,
  };

  localRules.email = {
    required,
    email,
  };

  localRules.phone = {
    required,
  };

  localRules.address = {
    required,
  };

  localRules.city = {
    required,
  };

  localRules.postal = {
    required,
  };

  localRules.paymentMethod = {
    required,
  };

  localRules.termsAndConditions = {
    required,
    mustBeTrue: sameAs(true),
  };

  return localRules;
});

const validation = useVuelidate(validationRules, checkoutFormData);

function submitPayment() {
  validation.value.$touch();
  if (validation.value.$invalid) {
    nextTick(() => {
      const firstInvalidInput = document.querySelector('.is-invalid');
      if (firstInvalidInput) {
        window.scrollTo({
          top: firstInvalidInput.getBoundingClientRect().top + window.scrollY - 100,
          behavior: 'smooth',
        });
      }
    });
    return;
  }

  if (submitted.value) {
    return;
  }
  submitted.value = true;

  axios
    .post('/api/orders', checkoutFormData.value)
    .then((response) => {
      if (checkoutFormData.value.paymentMethod === 'stripe') {
        router.push({
          name: 'StripePaymentPage',
          params: {
            uuid: response.data.uuid,
          },
        });
      } else {
        router.push({ name: 'OrderSuccessPage', params: { uuid: response.data.uuid } });
      }
    })
    .catch(() => {
      router.push({ name: 'PaymentFailedPage' });
    })
    .finally(() => {
      submitted.value = false;
    });
}

onMounted(() => {
  fetchProductDetails().then(() => {
    if (router.currentRoute.value.params.quantity) {
      quantity.value = router.currentRoute.value.params.quantity;
    } else {
      quantity.value = 1;
    }

    checkoutFormData.value.amount = parseInt(quantity.value);
  });
});

function fetchProductDetails() {
  return axios
    .get(`/api/products/${router.currentRoute.value.params.id}`)
    .then((response) => {
      product.value = response.data;
    })
    .catch(() => {
      notFound.value = true;
    });
}
</script>
